// reach_ui/src/components/VastGPT/AiKnowledge.js
/* eslint-disable import/no-unresolved */
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { withRouter } from 'react-router-dom'
import {
  sendMessageToOpenAi,
  selectChat,
  setLoading,
  receiveChat,
} from './redux'
import { create, list, read, update } from 'actions/data'
import { createTemplate } from 'actions/template'
import { toggleDialog } from 'actions/view'
import { DATA_API_URL } from 'utils/AllURLs'
import ChatInput from './ChatInput'
import ChatDrawer from './ChatDrawer'
import ChatHeader from './ChatHeader'
import ChatMessages from './ChatMessages'
import RelevantInfo from './RelevantInfo'
import ToolCallComponent from './ToolCallComponent'
import Ellipsis from './Ellipsis'
import './ChatComponent.scss'
import { createSelector } from 'reselect'

const getSelectedChatId = state => state.VastGPT.selectedChatId
const getOpenAiChats = state => state.data.openaichat

export const getChat = createSelector(
  [getSelectedChatId, getOpenAiChats],
  (selectedChatId, openAiChat) => openAiChat?.[selectedChatId],
)

export const getTitle = createSelector(
  [getSelectedChatId, getOpenAiChats],
  (selectedChatId, openAiChat) =>
    openAiChat?.[selectedChatId]?.title ||
    openAiChat?.[selectedChatId]?.messages?.[1]?.content ||
    'New chat',
)

export const getAiKnowledgeProps = createSelector(
  [
    state => state.VastGPT.messages,
    state => state.VastGPT.loadingResponse,
    state => state.auth?.session?.idToken?.payload?.given_name,
    state => state.auth?.session?.idToken?.payload?.family_name,
    state => state.VastGPT.relevantInfo,
    state => state.auth?.session?.idToken?.payload?.['custom:vr_role'],
    getSelectedChatId,
    getChat,
    getTitle,
  ],
  (
    messages,
    loadingResponse,
    given_name,
    family_name,
    relevantInfo,
    customVrRole,
    selectedChatId,
    chat,
    title,
  ) => ({
    messages: messages?.slice(1),
    loadingResponse,
    given_name,
    family_name,
    relevantInfo,
    customVrRole,
    selectedChatId,
    chat,
    title,
  }),
)

const mapStateToProps = state => getAiKnowledgeProps(state)

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      toggleDialog,
      create,
      createTemplate,
      list,
      selectChat,
      receiveChat,
      setLoading,
      read,
      update,
      sendMessageToOpenAi,
    },
    dispatch,
  )

class AiKnowledge extends Component {
  state = {
    pollingTimeout: null,
    currentInterval: 250,
    isInputDisabled: false,
    drawerOpen: false,
  }

  componentDidMount() {
    console.log('AiKnowledge componentDidMount()')
    if (this.props.customVrRole === 'acctowner') {
      this.props.list('openaichat', null, `${DATA_API_URL}/openaichat`)
    } else {
      this.props.list(
        'openaichat/username-entity',
        null,
        `${DATA_API_URL}/openaichat/username-entity`,
      )
    }

    this.props.list('user', 'user_name')

    // Start polling if there's a selected chat
    this.startPollingIfNeeded()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedChatId !== this.props.selectedChatId) {
      this.stopPolling()
      this.handleChatChange()
    }
  }

  componentWillUnmount() {
    console.log('AiKnowledge componentWillUnmount()')
    this.stopPolling()
  }

  startPollingIfNeeded = () => {
    const { selectedChatId, chat } = this.props
    if (selectedChatId && chat?.status === 'REQUEST') {
      this.startPolling()
    }
  }

  startPolling = () => {
    if (!this.state.pollingTimeout) {
      this.pollExistingChat()
    }
  }

  stopPolling = () => {
    if (this.state.pollingTimeout) {
      clearTimeout(this.state.pollingTimeout)
      this.setState({ pollingTimeout: null, currentInterval: 250 })
    }
  }

  pollExistingChat = async () => {
    console.log('pollExistingChat()')
    const { selectedChatId, chat, read, receiveChat, setLoading, update } =
      this.props
    const increaseFactor = 1.1
    const maxInterval = 3000 // 3 seconds
    const maxPollingTime = 9 * 60 // 9 minutes in seconds

    if (chat && chat.status === 'REQUEST') {
      const lastUpdateAttempt =
        typeof chat.lastUpdateAttempt === 'number'
          ? chat.lastUpdateAttempt
          : chat.updated_date
      const currentTime = Math.floor(Date.now() / 1000)

      console.log(
        'Polling - Time since last update:',
        currentTime - lastUpdateAttempt,
        'seconds',
      )
      console.log('Max polling time:', maxPollingTime, 'seconds')

      if (currentTime - lastUpdateAttempt > maxPollingTime) {
        console.log('Max polling time reached, updating status to FAILURE')
        await update(
          {
            id: selectedChatId,
            status: 'FAILURE',
            errorMessage: 'Chat processing timed out after 9 minutes.',
            lastUpdateAttempt: currentTime,
          },
          'openaichat',
          null,
          '',
          null,
          true,
          'none',
          `${DATA_API_URL}/openaichat/${selectedChatId}`,
        )
        setLoading(false)
        this.stopPolling()
        return
      }

      setLoading('loading')

      try {
        const readPayload = await read(
          selectedChatId,
          'openaichat',
          null,
          null,
          'object',
          null,
          null,
          `${DATA_API_URL}/openaichat/${selectedChatId}`,
        )

        if (
          readPayload.status === 'FAILURE' ||
          readPayload.status === 'SUCCESS'
        ) {
          setLoading(false)
          receiveChat(readPayload)
          this.stopPolling()
        } else {
          setLoading('loadingWithText')
          if (readPayload.messages?.length) {
            receiveChat(readPayload)
          }
          // Schedule next poll with increased interval
          const nextInterval = Math.min(
            this.state.currentInterval * increaseFactor,
            maxInterval,
          )
          const timeout = setTimeout(
            () => this.pollExistingChat(),
            nextInterval,
          )
          this.setState({
            pollingTimeout: timeout,
            currentInterval: nextInterval,
          })
        }
      } catch (error) {
        console.error('Error polling chat:', error)
        setLoading(false)
        this.stopPolling()
      }
    }
  }

  handleChatChange = async () => {
    const { selectedChatId, read, receiveChat } = this.props

    if (selectedChatId) {
      try {
        const readPayload = await read(
          selectedChatId,
          'openaichat',
          null,
          null,
          'object',
          null,
          null,
          `${DATA_API_URL}/openaichat/${selectedChatId}`,
        )

        if (readPayload.status === 'REQUEST') {
          this.startPolling()
        } else {
          receiveChat(readPayload)
        }
      } catch (error) {
        console.error('Error reading chat:', error)
      }
    }
  }

  toggleDrawer = open => {
    this.setState({ drawerOpen: open })
  }

  handleNewChat = () => {
    this.props.selectChat(null)
  }

  render() {
    const {
      messages,
      loadingResponse,
      given_name,
      family_name,
      relevantInfo,
      title,
      selectedChatId,
    } = this.props

    return (
      <div className="AiChatComponentWrapper">
        <ChatHeader
          title={title}
          loadingResponse={loadingResponse}
          selectedChatId={selectedChatId}
          onOpenDrawer={() => this.toggleDrawer(true)}
          onClose={() => {
            this.props.selectChat(null)
            this.props.toggleDialog({ open: false, dialog: 'AiKnowledge' })
          }}
        />
        <ChatDrawer
          isOpen={this.state.drawerOpen}
          onClose={() => this.toggleDrawer(false)}
          onNewChat={this.handleNewChat}
          toggleDrawer={this.toggleDrawer}
        />
        <div className="AiChatComponent">
          {loadingResponse === 'initial' ? (
            <>
              {!relevantInfo?.length ? (
                <div style={{ padding: 30 }}>
                  Loading
                  <Ellipsis />
                </div>
              ) : null}
              {relevantInfo?.length ? (
                <RelevantInfo relevantInfo={relevantInfo} />
              ) : null}
            </>
          ) : (
            <>
              {relevantInfo.length ? (
                <RelevantInfo relevantInfo={relevantInfo} />
              ) : null}
              <ChatMessages
                messages={messages}
                given_name={given_name}
                family_name={family_name}
                loadingResponse={loadingResponse}
                toolCallComponent={(message, index) => (
                  <ToolCallComponent
                    message={message}
                    isLastMessage={index === messages.length - 1}
                    loadingResponse={loadingResponse}
                    create={this.props.create}
                    createTemplate={this.props.createTemplate}
                  />
                )}
              />
            </>
          )}
        </div>
        <ChatInput
          loadingResponse={loadingResponse}
          selectedChatId={selectedChatId}
          setDisabled={isDisabled => this.setState({ isInputDisabled: isDisabled })}
          startPolling={this.startPolling}
          isDisabled={this.state.isInputDisabled || loadingResponse}
        />
      </div>
    )
  }
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(AiKnowledge),
)
